import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Set default timeout for all axios requests
axios.defaults.timeout = 30000;

// Set up axios interceptors for retry logic
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Don't retry if we've already retried or if it's a 401/403 (auth error)
    if (originalRequest._retry || 
        error.response?.status === 401 || 
        error.response?.status === 403) {
      return Promise.reject(error);
    }
    
    // Retry on network errors, timeouts, or 5xx server errors
    const shouldRetry = !error.response || 
                        error.code === 'ECONNABORTED' ||
                        error.code === 'ERR_NETWORK' ||
                        (error.response.status >= 500 && error.response.status < 600);
    
    if (shouldRetry) {
      originalRequest._retry = true;
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;
      
      if (originalRequest._retryCount <= 2) {
        console.log(`Retrying request (attempt ${originalRequest._retryCount + 1}):`, originalRequest.url);
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, 1000 * originalRequest._retryCount));
        return axios(originalRequest);
      }
    }
    
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);

export const AuthProvider = ({ children, skipInitialCheck = false }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(!skipInitialCheck);

  useEffect(() => {
    const initAuth = async () => {
      // Skip auth check if we're processing OAuth callback
      if (skipInitialCheck) {
        console.log('Skipping initial auth check for OAuth callback');
        return;
      }
      
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error('Auth init error:', error);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    initAuth();
  }, [token, skipInitialCheck]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const signup = async (name, email, password, phone, language_preference, selected_plan) => {
    const response = await axios.post(`${API}/auth/signup`, {
      name,
      email,
      password,
      phone,
      language_preference,
      selected_plan
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  // Function to set auth data from OAuth callback
  const setAuthData = (accessToken, userData) => {
    localStorage.setItem('token', accessToken);
    setToken(accessToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        return response.data;
      } catch (error) {
        console.error('Refresh user error:', error);
      }
    }
    return null;
  };

  const getAuthHeaders = () => {
    // Get token from state or localStorage as fallback
    const currentToken = token || localStorage.getItem('token');
    return {
      Authorization: `Bearer ${currentToken}`
    };
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      signup, 
      logout, 
      refreshUser,
      setAuthData,
      getAuthHeaders 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
